import { ReactElement } from 'react';

import { Translate, useFormController } from '@dreryk/react-common';
import {
   FileUpload,
   FileUploadTriggerVariant,
   FormRouteLeavingPrompt,
   Heading,
   StyledFlex,
   StyledMaxWidth,
   TextInput,
} from '@dreryk/react-components';

import { onlinePaymentsIntegrationTranslationKeys } from '../../translations';

import {
   OnlinePaymentsIntegrationFormBean,
   OnlinePaymentsIntegrationFormController,
} from './OnlinePaymentsIntegrationForm.types';
import {
   ACCEPTED_PDF_FILE_EXTENSIONS,
   ID_MAX_LENGTH,
   MAX_PDF_FILE_NAME_CHARACTERS,
   MAX_PDF_FILE_SIZE_IN_BYTES,
   MD5_MAX_LENGTH,
   VALIDATION_RULES,
} from './OnlinePaymentsIntegrationForm.constants';

type OnlinePaymentsIntegrationFormRenderProps = Pick<
   OnlinePaymentsIntegrationFormController,
   'handleSubmit' | 'formState'
>;

type OnlinePaymentsIntegrationFormProps = {
   readonly children: (renderProps: OnlinePaymentsIntegrationFormRenderProps) => ReactElement;
};

export const OnlinePaymentsIntegrationForm = (props: OnlinePaymentsIntegrationFormProps): ReactElement => {
   const { register, handleSubmit, formState } = useFormController<OnlinePaymentsIntegrationFormBean>({});

   return (
      <>
         <StyledFlex as={'form'} direction={'column'} gap={24} width={'500px'}>
            <Heading
               testId={'integrationHeader'}
               content={onlinePaymentsIntegrationTranslationKeys.integrationWithPayu}
               margin={0}
               type={'h2'}
            />
            <StyledMaxWidth maxWidth={'400px'}>
               <TextInput
                  controller={register('posId', { required: true, maxLength: ID_MAX_LENGTH })}
                  testId={'posId'}
                  label={onlinePaymentsIntegrationTranslationKeys.form.fields.posId.label}
               />
            </StyledMaxWidth>
            <StyledMaxWidth maxWidth={'400px'}>
               <TextInput
                  controller={register('secondKey', { required: true, maxLength: MD5_MAX_LENGTH })}
                  testId={'secondKey'}
                  label={onlinePaymentsIntegrationTranslationKeys.form.fields.secondKey.label}
               />
            </StyledMaxWidth>
            <StyledMaxWidth maxWidth={'400px'}>
               <TextInput
                  controller={register('clientId', { required: true, maxLength: ID_MAX_LENGTH })}
                  testId={'clientId'}
                  label={onlinePaymentsIntegrationTranslationKeys.form.fields.clientId.label}
               />
            </StyledMaxWidth>
            <StyledMaxWidth maxWidth={'400px'}>
               <TextInput
                  controller={register('clientSecret', { required: true, maxLength: MD5_MAX_LENGTH })}
                  testId={'clientSecret'}
                  label={onlinePaymentsIntegrationTranslationKeys.form.fields.clientSecret.label}
               />
            </StyledMaxWidth>
            <div>
               <Heading
                  testId={'integrationHeader'}
                  content={onlinePaymentsIntegrationTranslationKeys.onlinePaymentsTermsOfService}
                  margin={0}
                  type={'h2'}
               />
               <Translate>{onlinePaymentsIntegrationTranslationKeys.termsOfServiceDisclaimer}</Translate>
            </div>
            <StyledMaxWidth maxWidth={'450px'}>
               <FileUpload
                  controller={register('termsOfService', VALIDATION_RULES)}
                  label={onlinePaymentsIntegrationTranslationKeys.form.fields.termsOfService.label}
                  triggerLabel={onlinePaymentsIntegrationTranslationKeys.form.fields.termsOfService.chooseFile}
                  acceptedExtensions={ACCEPTED_PDF_FILE_EXTENSIONS}
                  triggerVariant={FileUploadTriggerVariant.BUTTON_SECONDARY}
                  maxFileSizeInBytes={MAX_PDF_FILE_SIZE_IN_BYTES}
                  maxFileNameLength={MAX_PDF_FILE_NAME_CHARACTERS}
                  placeholder={onlinePaymentsIntegrationTranslationKeys.form.fields.termsOfService.choosePdfFile}
                  testId={'termsOfService'}
               >
                  <FileUpload.Dropzone testId={'termsOfService'} hideIfFilled />
               </FileUpload>
            </StyledMaxWidth>
         </StyledFlex>
         <FormRouteLeavingPrompt formState={formState} />
         {props.children({ handleSubmit: handleSubmit, formState: formState })}
      </>
   );
};
